'use client';

/**
 * Loads the Google AdSense script tag once when ALL of:
 *   1. site config has `ads.adsense_enabled = true` and a valid publisher ID
 *   2. cookie-consent provider has hydrated and the user opted in to the
 *      "advertising" category (GDPR/CCPA — Google's EU User Consent Policy
 *      forbids loading the AdSense bundle before consent).
 *
 * Why a client component? The publisher ID lives in the runtime site_settings
 * (admin-editable), so we can't bake it into a server-rendered <Script /> tag.
 * Loading via a client effect that runs after the SiteConfigProvider hydrates
 * keeps the toggle truly runtime-flippable.
 *
 * Idempotent: tracks whether the script element was already inserted via a
 * `data-ps-adsense` attribute on <html> so navigating between routes doesn't
 * load it twice. AdSense's own script also de-dupes itself, but belt + braces.
 */
import { useEffect } from 'react';
import { useSiteConfig } from '@/lib/site-config';
import { extractAdsensePublisherId } from '@/lib/adsense';
import { useCookieConsent } from '@/lib/cookie-consent';

export default function AdSenseScript() {
  const { config, ready } = useSiteConfig();
  const { advertising, hydrated } = useCookieConsent();

  useEffect(() => {
    if (!ready || !hydrated) return;
    const enabled = !!config['ads.adsense_enabled'];
    const publisherId = extractAdsensePublisherId(String(config['ads.adsense_publisher_id'] ?? ''));
    const html = document.documentElement;
    const allowed = enabled && !!publisherId && advertising;

    // Already inserted with the right publisher ID?
    if (html.dataset.psAdsense === publisherId && allowed) return;

    // Disabled / no id / consent withdrawn — leave stale tag for AdSense's
    // own dedupe but clear our marker so a future opt-in re-inserts cleanly.
    if (!allowed) {
      delete html.dataset.psAdsense;
      return;
    }

    const src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-${publisherId}`;
    const existing = Array.from(document.scripts).some((s) => s.src === src);
    if (existing) {
      html.dataset.psAdsense = publisherId;
      return;
    }

    // Insert. AdSense recommends `crossorigin="anonymous"` and async load.
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    script.crossOrigin = 'anonymous';
    script.dataset.psAdsense = '1';
    document.head.appendChild(script);

    html.dataset.psAdsense = publisherId;
  }, [ready, config, advertising, hydrated]);

  return null;
}
